/**
 * Checkout coupon verification.
 */
import { useCallback } from "react";
import { oauth } from "../utils";
import type {
  CheckoutState,
  AppliedDiscount,
} from "../components/shop/Checkout/types";
import type { Dispatch, SetStateAction } from "react";
import type { PublicViewContextValue } from "./useCheckoutState";

interface UseCheckoutCouponArgs {
  state: CheckoutState;
  setState: Dispatch<SetStateAction<CheckoutState>>;
  onCreateErrorPopup: (
    error_msg: string,
    onClose?: (() => void) | null,
  ) => unknown;
  context: PublicViewContextValue | undefined;
}

export function useCheckoutCoupon({
  state,
  setState,
  onCreateErrorPopup,
  context,
}: UseCheckoutCouponArgs) {
  const checkCoupon = useCallback(() => {
    const entityIdC = context?.publicViewEntity?.entityId;
    const entityOrder = context?.publicViewEntity?.entityOrder;
    if (!entityIdC || !entityOrder) return Promise.resolve(undefined);
    if (state.code === "") return Promise.resolve(undefined);

    return oauth("GET", "discount/verify", {
      parent_id: entityIdC,
      code: state.code,
      subtotal: state.orderTotal,
      currency_symbol: (entityOrder as { currency_symbol?: string })
        .currency_symbol,
      ...(state.order_contact?.contact_email && {
        email: state.order_contact.contact_email.trim(),
      }),
      ...(state.applied_discount && { code: state.applied_discount.code }),
    }).then(
      ({ json }: { json: { discount?: AppliedDiscount; error?: string } }) => {
        if (json.discount) {
          setState((s) => ({
            ...s,
            errors: { ...s.errors, discount: "" },
            applied_discount: json.discount as AppliedDiscount,
            avalara_summary: false,
          }));
        } else {
          let errMsg = json.error || "Unable to apply coupon";
          if (errMsg === "Invalid discount code")
            errMsg = "Sorry, your coupon code is invalid!";
          const onCloseErrPopup = () => {
            setState((s2) => ({
              ...s2,
              code: "",
              errors: { ...s2.errors, discount: "" },
            }));
          };
          if (errMsg) onCreateErrorPopup(errMsg, onCloseErrPopup);
          setState((s) => ({
            ...s,
            code: "",
            errors: { ...s.errors, discount: errMsg },
            avalara_summary: false,
          }));
        }
        return json;
      },
    );
  }, [
    context?.publicViewEntity?.entityId,
    context?.publicViewEntity?.entityOrder,
    state.code,
    state.orderTotal,
    state.order_contact?.contact_email,
    state.applied_discount,
    onCreateErrorPopup,
    setState,
  ]);

  return { checkCoupon };
}
